import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  SwipeableDrawer,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  styled,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useVideoSession } from './hooks/useVideoSession';
import VideoSession from './VideoSession';
import { API } from 'aws-amplify';
import {
  listVideoData,
  listSessionLists,
  listEventLists,
} from '../../graphql/queries';
import { grey } from '@mui/material/colors';
import { getVideoChips } from '../../utils/chipColors';
import CarSessionCard from './CarSessionCard';

const DRAWER_WIDTH = 320;
const DRAWER_BLEEDING = 56;

const StyledBox = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  transition: 'background-color 0.2s',
}));

const Puller = styled('div')(({ theme }) => ({
  width: 36,
  height: 150,
  backgroundColor: grey[300],
  borderTopLeftRadius: 8,
  borderBottomLeftRadius: 8,
  position: 'fixed',
  top: '50%',
  right: 0,
  transform: 'translateY(-50%)',
  zIndex: 1301,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  padding: '6px 6px',
  '&:hover': {
    backgroundColor: grey[400],
  },
}));

/**
 * Wrapper component for the enhanced session viewer
 * Integrates with existing navigation system
 */
function SessionViewerWrapper({
  selectedSession,
  liftCreatingSession,
  liftSelectedSession,
  liftComparisonVideos,
}) {
  const [layout, setLayout] = useState('side-by-side');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // Extract all data from selectedSession
  const {
    videoDataId,
    carNum,
    username,
    cameraAngle,
    sessionId,
    sessionName,
    trackConfig,
    eventId,
    eventName,
    eventVenue,
    eventDate,
    hasVideo,
    hasTelemetry,
    hasLaptimes,
  } = selectedSession;

  // Use the video session hook
  const { sessionData, isLoading, error, reload } =
    useVideoSession(videoDataId);

  // Load related videos when we have trackConfig
  useEffect(() => {
    if (trackConfig) {
      loadRelatedVideos();
    }
  }, [trackConfig]);

  const loadRelatedVideos = async () => {
    if (!trackConfig) return;

    setLoadingRelated(true);
    try {
      // First get all sessions with this track config
      const sessionsResult = await API.graphql({
        query: listSessionLists,
        variables: {
          filter: {
            trackConfig: {
              eq: trackConfig,
            },
          },
        },
      });

      // Get all session IDs
      const sessionIds = sessionsResult.data.listSessionLists.items.map(
        (session) => session.id
      );

      if (sessionIds.length === 0) {
        setRelatedVideos([]);
        return;
      }

      // Then get all videos for these sessions
      const videosResult = await API.graphql({
        query: listVideoData,
        variables: {
          filter: {
            or: sessionIds.map((id) => ({
              sessionId: {
                eq: id,
              },
            })),
          },
        },
      });

      // Filter out current video and sort by date
      const videos = videosResult.data.listVideoData.items
        .filter((v) => v.id !== videoDataId)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      // Fetch all sessions for these videos
      const sessionIdsForVideos = Array.from(
        new Set(videos.map((v) => v.sessionId).filter(Boolean))
      );
      let sessionMap = {};
      if (sessionIdsForVideos.length > 0) {
        const sessionsResult = await API.graphql({
          query: listSessionLists,
          variables: {
            filter: {
              or: sessionIdsForVideos.map((id) => ({ id: { eq: id } })),
            },
          },
        });
        sessionMap = Object.fromEntries(
          sessionsResult.data.listSessionLists.items.map((s) => [s.id, s])
        );
      }

      // Fetch all events for these sessions
      const eventIdsForSessions = Array.from(
        new Set(
          Object.values(sessionMap)
            .map((s) => s.eventId)
            .filter(Boolean)
        )
      );
      let eventMap = {};
      if (eventIdsForSessions.length > 0) {
        const eventsResult = await API.graphql({
          query: listEventLists,
          variables: {
            filter: {
              or: eventIdsForSessions.map((id) => ({ eventId: { eq: id } })),
            },
          },
        });
        eventMap = Object.fromEntries(
          eventsResult.data.listEventLists.items.map((e) => [e.eventId, e])
        );
      }

      // Enrich videos with session and event info
      const enrichedVideos = videos.map((video) => {
        const session = sessionMap[video.sessionId] || {};
        const event = eventMap[session.eventId] || {};
        return {
          ...video,
          _session: session,
          _event: event,
        };
      });
      setRelatedVideos(enrichedVideos);
    } catch (error) {
      console.error('Error loading related videos:', error);
      setRelatedVideos([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleBack = () => {
    liftSelectedSession(null);
    liftCreatingSession(0); // Navigate back to event list
  };

  const handleVideoSelect = (videoId) => {
    // Pass session metadata along with video IDs for comparison
    const sessionMetadata = {
      trackConfig,
      eventVenue,
      carNum,
      username,
      eventName,
      eventDate,
    };
    liftComparisonVideos(videoDataId, videoId, sessionMetadata);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleEdgeClick = (event) => {
    event.stopPropagation();
    setDrawerOpen(!drawerOpen);
  };

  if (!videoDataId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No video ID provided. Please select a session from the event list.
        </Alert>
        <Button variant="outlined" onClick={handleBack} sx={{ mt: 2 }}>
          Back to Events
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', position: 'relative' }}>
      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header/Controls */}
        <Paper
          elevation={1}
          sx={{ p: 1, flexShrink: 0, borderBottom: '1px solid #eee' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>

            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Enhanced Session Viewer
            </Typography>

            <IconButton
              onClick={toggleDrawer(true)}
              size="small"
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Layout</InputLabel>
              <Select
                value={layout}
                onChange={(e) => setLayout(e.target.value)}
                label="Layout"
              >
                <MenuItem value="side-by-side">Side by Side</MenuItem>
                <MenuItem value="stacked">Stacked</MenuItem>
                <MenuItem value="video-only">Video Only</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={reload}
              disabled={isLoading}
              size="small"
            >
              Reload
            </Button>
          </Box>

          {/* Status Display */}
          {videoDataId && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                fontSize: '0.875rem',
              }}
            >
              <Typography variant="caption">
                🏎️ {username || 'Unknown Driver'}
              </Typography>
              <Typography variant="caption">
                🏁 {sessionData?.capabilities?.lapCount || 0} Laps
              </Typography>
              <Typography variant="caption">
                🏟️ {eventVenue || 'Unknown Track'}
              </Typography>
              <Typography variant="caption">
                ⚙️ {trackConfig || 'Default Config'}
              </Typography>
              <Typography variant="caption">
                📹 Video: {hasVideo ? '✅' : '❌'}
              </Typography>
              <Typography variant="caption">
                📊 Telemetry: {hasTelemetry ? '✅' : '❌'}
              </Typography>
              <Typography variant="caption">
                🎯 Segments:{' '}
                {sessionData?.capabilities?.hasSegments ? '✅' : '❌'}
              </Typography>
              <Typography variant="caption">
                📏 Distance:{' '}
                {sessionData?.capabilities?.hasDistance ? '✅' : '❌'}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Main Content Area */}
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <VideoSession
            sessionData={sessionData}
            isLoading={isLoading}
            error={error}
            layout={layout}
            showTelemetry={true}
            showNavigation={true}
            onReload={reload}
          />
        </Box>

        {/* Debug Info (if in development) */}
        {process.env.NODE_ENV === 'development' && videoDataId && (
          <Paper sx={{ p: 1, m: 1, backgroundColor: 'grey.100' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Debug:</strong> VideoID: {videoDataId} | Loading:{' '}
              {isLoading ? 'Yes' : 'No'} | Error:{' '}
              {error ? error.message : 'None'} | Layout: {layout}
            </Typography>

            {sessionData && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 2 }}
              >
                | Laps: {sessionData.lapData?.length || 0} | Telemetry Points:{' '}
                {sessionData.telemetry?.locations?.length || 0}
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Drawer Pull Tab - Only the tab, not a full edge */}
      <Puller onClick={handleEdgeClick}>
        <Typography
          variant="caption"
          sx={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            fontWeight: 600,
            fontSize: '1rem',
            color: 'text.secondary',
            userSelect: 'none',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            letterSpacing: '0.03em',
          }}
        >
          {drawerOpen ? 'Close' : 'Related Sessions'}
        </Typography>
      </Puller>

      {/* Right Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={DRAWER_BLEEDING}
        disableSwipeToOpen={false}
        keepMounted
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="h6">Related Videos</Typography>
          <Typography variant="caption" color="text.secondary">
            {trackConfig || 'Loading...'}
          </Typography>
        </Box>

        <Box sx={{ p: 2, overflow: 'auto' }}>
          {loadingRelated ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : relatedVideos.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No other videos found for this track configuration.
            </Typography>
          ) : (
            relatedVideos.map((video) => {
              return (
                <Box key={video.id} sx={{ mb: 2, px: 1 }}>
                  <CarSessionCard
                    car={video}
                    session={{
                      sessionName: video._session.sessionName,
                      trackConfig: video._session.trackConfig,
                    }}
                    group={{
                      eventVenue: video._event.eventVenue,
                      eventName: video._event.eventName,
                    }}
                    isMobile={false}
                    viewMode={layout === 'track' ? 'track' : 'event'}
                    cardWidth="100%"
                    bestLap={
                      Array.isArray(video.laptimeData) &&
                      video.laptimeData.length > 0
                        ? Math.min(
                            ...video.laptimeData
                              .map((lap) => Number(lap.duration))
                              .filter(Boolean)
                          )
                        : undefined
                    }
                    totalLaps={
                      Array.isArray(video.laptimeData)
                        ? video.laptimeData.length
                        : undefined
                    }
                    onPlay={() => handleVideoSelect(video.id)}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </SwipeableDrawer>
    </Box>
  );
}

export default SessionViewerWrapper;
